/* eslint-disable @typescript-eslint/naming-convention */
import { AfterViewInit, Component, OnInit } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { createSelector, Store } from '@ngrx/store';
import { get as _get } from 'lodash';
import { Observable } from 'rxjs';
import { filter, map, take, withLatestFrom } from 'rxjs/operators';

import { addVehicleInfoQuery, addVehicleQuoteQuery } from '@amfam/policy/add-vehicle/data-access';
import { PlautoService } from '@amfam/policy/data-access';
import { PolicySharedService, VehicleQuoteService } from '@amfam/policy/shared';
import { VehicleCoveragesFacade } from '@amfam/policy/vehicle-coverages/data-access';
import { vehicleQuery } from '@amfam/policy/vehicle/data-access';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import { IQuote, PaymentScheduleItem, QuoteSummaryModel } from '@amfam/shared/models';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { CopyService, UtilService } from '@amfam/shared/utility/shared-services';
import { DsModalService, LoadingSpinnerService } from '@amfam/ui-kit';

import { addVehicleFeatureQuery } from '../+state/add-vehicle-feature.selectors';
import { AddVehicleFeatureService } from '../services/add-vehicle-feature.service';
import { VehicleQuoteSummmaryComponent } from './../vehicle-quote-summmary/vehicle-quote-summmary.component';

@Component({
  selector: 'ds-afi-vehicle-quote-summary',
  templateUrl: './afi-vehicle-quote-summary.component.html',
  styleUrls: ['./afi-vehicle-quote-summary.component.scss'],
  standalone: false
})
export class AfiVehicleQuoteSummaryComponent
  extends VehicleQuoteSummmaryComponent
  implements OnInit, AfterViewInit
{
  quote$: Observable<{ new: IQuote; current: IQuote }>;
  vehiclesLeft$: Observable<string[]>;
  vehiclesLeftCommaSeparated$: Observable<string>;
  futurePayments$: Observable<QuoteSummaryModel[]>;

  constructor(
    protected store: Store,
    protected route: ActivatedRoute,
    protected formBuilder: UntypedFormBuilder,
    protected spinnerService: LoadingSpinnerService,
    protected copyService: CopyService,
    protected autoPolicyService: PlautoService,
    protected modalService: DsModalService,
    protected addVehicleService: AddVehicleFeatureService,
    protected utilService: UtilService,
    protected roleService: ImpersonateRolesService,
    protected vehicleCoverageFacade: VehicleCoveragesFacade,
    protected vehicleQuoteService: VehicleQuoteService,
    protected analyticsFacade: AnalyticsFacade,
    protected policySharedService: PolicySharedService
  ) {
    super(
      store,
      route,
      formBuilder,
      spinnerService,
      copyService,
      autoPolicyService,
      modalService,
      addVehicleService,
      utilService,
      roleService,
      vehicleCoverageFacade,
      policySharedService
    );
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.quote$ = this.newVehicleRate$.pipe(
      filter(rate => !!rate),
      map(rate =>
        this.vehicleQuoteService.mapQuotes(
          +rate.priorMonthly,
          +rate.priorCost,
          +rate.monthlyPremium,
          +rate.totalCost,
          rate.termMonths
        )
      )
    );
    this.vehiclesLeft$ = this.store.select(addVehicleInfoQuery.selectNewVehicleList).pipe(
      withLatestFrom(this.store.select(addVehicleInfoQuery.selectNewVehicleVin)),
      filter(vehicles => !!vehicles),
      map(([vehicles, vin]) => vehicles.filter(v => v.vin !== vin).map(v => v.description))
    );
    this.vehiclesLeftCommaSeparated$ = this.vehiclesLeft$.pipe(
      map(vehicles => vehicles.join(', '))
    );

    this.store
      .select(
        createSelector(
          vehicleQuery.selectNewVehicle,
          addVehicleQuoteQuery.selectNewVehicleWorkOrderNumber,
          (newVehicle, workOrderNumber) => ({
            newVehicle: newVehicle,
            workOrderNumber: workOrderNumber
          })
        )
      )
      .pipe(take(1))
      .subscribe(state => {
        this.analyticsFacade.trackEvent({
          event: 'add_vehicle_quote_summary',
          form_name: 'Add Vehicle Flow',
          vehicle_make: _get(state, 'newVehicle.make', ''),
          vehicle_model: _get(state, 'newVehicle.model', ''),
          vehicle_year: _get(state, 'newVehicle.year', ''),
          quote_id: _get(state, 'workOrderNumber', ''),
          product_line: 'auto'
        });
      });

    this.futurePayments$ = this.store.select(addVehicleFeatureQuery.selectFuturePayments).pipe(
      filter(payments => !!payments),
      map((payments: PaymentScheduleItem[]) => this.vehicleQuoteService.mapPayments(payments))
    );
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
  }
}
